import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { smsService } from '../../services/smsService';
import { emailService } from '../../services/emailService';
import { ACTIONS, logAction } from '../../services/actionLog'
import './InspectionBooking.css';

interface InspectionVendor {
  id: string;
  vendor_name: string;
  primary_state: string;
  phone: string;
  email: string;
}

interface InspectionBookingProps {
  dealId: string;
  /** Who is booking. Passed in so the audit row can name them. */
  actorId?: string
  sellerLocation: string;
  vehicleInfo: {
    year: number;
    make: string;
    model: string;
    vin: string;
  };
  sellerInfo: {
    name: string;
    phone: string;
    email: string;
  };
  onBookingComplete?: () => void;
}

export const InspectionBooking: React.FC<InspectionBookingProps> = ({
  dealId,
  actorId,
  sellerLocation,
  vehicleInfo,
  sellerInfo,
  onBookingComplete
}) => {
  const [bookingStatus, setBookingStatus] = useState<'loading' | 'offer' | 'auto' | 'manual' | 'success' | 'error'>('loading');
  const [vendors, setVendors] = useState<InspectionVendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<InspectionVendor | null>(null);
  const [suggestedDate, setSuggestedDate] = useState('');
  const [suggestedTime, setSuggestedTime] = useState('09:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoBookResult, setAutoBookResult] = useState<any>(null);
  // What did NOT go out. Empty is the good case; anything in here is something
  // a person has to do by hand before an inspector turns up.
  const [deliveryWarnings, setDeliveryWarnings] = useState<string[]>([]);

  useEffect(() => {
    initializeInspectionBooking();
  }, []);

  const extractState = (location: string): string => {
    // Extract state abbreviation from location
    // Expected format: "Street, Suburb STATE POSTCODE"
    const stateMatch = location?.match(/\s([A-Z]{2,3})\s/);
    return stateMatch ? stateMatch[1] : '';
  };

  const initializeInspectionBooking = async () => {
    try {
      const state = extractState(sellerLocation);

      // Fetch active vendors — prefer the seller's state, else any active vendor
      let query = supabase.from('inspection_vendors').select('*').eq('active', true);
      if (state) query = query.eq('primary_state', state);
      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      let vendorList = data || [];
      // Fallback: if no vendor in the seller's state, offer all active vendors
      if (vendorList.length === 0 && state) {
        const { data: allData } = await supabase
          .from('inspection_vendors')
          .select('*')
          .eq('active', true);
        vendorList = allData || [];
      }

      setVendors(vendorList);

      if (vendorList.length > 0 && state) {
        // Auto-booking available for a same-state vendor
        setBookingStatus('offer');
      } else {
        // Manual booking required
        setBookingStatus('manual');
      }

      // Set default inspection date to 3 business days from now
      setDefaultInspectionDate();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize booking');
      setBookingStatus('error');
    }
  };

  /**
   * §1.3: "booking completed within 24-48 hours".
   *
   * The default was three business days out, which is outside the window before
   * anyone touches it. Two business days lands inside 48 hours on any weekday
   * and is the first date that clears a weekend.
   */
  const setDefaultInspectionDate = () => {
    const date = new Date();
    let daysToAdd = 2;

    while (daysToAdd > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // Skip weekends
        daysToAdd--;
      }
    }

    setSuggestedDate(date.toISOString().split('T')[0]);
  };

  const attemptAutoBook = async (vendor: InspectionVendor) => {
    try {
      // Simulate auto-booking (in production, this would call vendor's API)
      const bookingRef = `AB-${dealId.substring(0, 8)}-${Date.now()}`;

      // Update deal with auto-booking details
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          inspection_vendor_id: vendor.id,
          inspection_booking_method: 'auto',
          inspection_booking_reference: bookingRef,
          inspection_booked_at: new Date().toISOString(),
          inspection_status: 'pending'
        })
        .eq('id', dealId);

      if (updateError) throw updateError;

      setAutoBookResult({
        vendor: vendor.vendor_name,
        reference: bookingRef,
        phone: vendor.phone,
        email: vendor.email,
        method: 'auto'
      });

      /* Record it. Booking an inspection was one of the actions that left no trace at
         all, which is why the card could not say it had happened and nobody could tell
         who had booked it. */
      void logAction({
        dealId,
        action: ACTIONS.INSPECTION_BOOKING_INITIATED,
        actorId: actorId || null,
        details: { method: 'auto' },
      })
      setBookingStatus('success');

      setTimeout(() => {
        if (onBookingComplete) onBookingComplete();
      }, 3000);

    } catch (err) {
      console.error('Auto-booking failed:', err);
      // Fall back to manual booking
      setBookingStatus('manual');
      setVendors([vendor]); // Keep the vendor for manual selection
    }
  };

  const handleManualBooking = async () => {
    if (!selectedVendor || !suggestedDate) {
      setError('Please select a vendor and date');
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingRef = `MB-${dealId.substring(0, 8)}-${Date.now()}`;

      // Update deal with manual booking details
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          inspection_vendor_id: selectedVendor.id,
          inspection_booking_method: 'manual',
          inspection_booking_reference: bookingRef,
          inspection_booked_at: new Date().toISOString(),
          inspection_status: 'pending'
        })
        .eq('id', dealId);

      if (updateError) throw updateError;

      // Tell the vendor and the seller. Neither was ever sent: the vendor
      // invitation was a TODO and sendInspectionSMS had no call sites anywhere
      // in the codebase, so a booking existed only as a row.
      const scheduled = suggestedTime
        ? `${suggestedDate} ${suggestedTime}`
        : suggestedDate;

      await supabase
        .from('deals')
        .update({ inspection_scheduled_for: new Date(`${suggestedDate}T${suggestedTime || '09:00'}`).toISOString() })
        .eq('id', dealId);

      // Both of these used to be `.catch(() => undefined)` — the result was
      // thrown away and the screen said the booking had gone out regardless.
      // If the vendor was never emailed, nobody is coming to look at the car,
      // and the person standing in front of this screen is the only one who
      // can fix that. So the outcome is captured and shown.
      const notSent: string[] = [];

      /*
       * No address, no email. It used to send anyway with the words "see deal" in place
       * of the address - an instruction the inspector cannot follow, having no account
       * here and no link. Better to stop and say so than to send a request that only
       * produces a phone call asking where the car is.
       */
      const inspectionAddress = (sellerLocation || '').trim()
      if (!inspectionAddress) {
        notSent.push(`${selectedVendor.vendor_name} was not emailed: this deal has no seller address, and an inspection request without one is not something anyone can act on. Add the address to the deal and book again.`)
      } else if (selectedVendor.email) {
        const vendorEmail = await emailService
          .send(
            selectedVendor.email,
            `Inspection request — ${bookingRef}`,
            `Please confirm a pre-purchase inspection.\n\nReference: ${bookingRef}\nRequested: ${scheduled}\nLocation: ${inspectionAddress}\n\nDirect Auto`
          )
          .catch(() => ({ success: false, message: 'Email failed' }) as { success: boolean; message: string });
        if (!vendorEmail?.success) {
          notSent.push(`${selectedVendor.vendor_name} was not emailed (${vendorEmail?.message || 'unknown error'}) — call them`);
        }
      } else {
        notSent.push(`${selectedVendor.vendor_name} has no email address on file — call them`);
      }

      if (sellerInfo.phone) {
        const sellerSms = await smsService
          .sendInspectionSMS(sellerInfo.phone, sellerInfo.name || '', dealId, scheduled)
          .catch(() => ({ success: false, message: 'SMS failed' }) as { success: boolean; message: string });
        if (!sellerSms?.success) {
          notSent.push(`the seller was not texted (${sellerSms?.message || 'unknown error'})`);
        }
      } else {
        notSent.push('the seller has no mobile number on file');
      }

      setDeliveryWarnings(notSent);
      void logAction({
        dealId,
        action: ACTIONS.INSPECTION_BOOKING_INITIATED,
        actorId: actorId || null,
        details: { method: 'manual' },
      })
      setBookingStatus('success');
      setAutoBookResult({
        vendor: selectedVendor.vendor_name,
        reference: bookingRef,
        phone: selectedVendor.phone,
        email: selectedVendor.email,
        method: 'manual'
      });

      setTimeout(() => {
        if (onBookingComplete) onBookingComplete();
      }, 2000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Booking failed';
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const retry = () => {
    setError(null);
    setBookingStatus('loading');
    initializeInspectionBooking();
  };

  if (bookingStatus === 'loading') {
    return (
      <div className="inspection-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Finding available inspectors...</p>
        </div>
      </div>
    );
  }

  if (bookingStatus === 'error') {
    return (
      <div className="inspection-container">
        <div className="error-state">
          <h3>Booking Error</h3>
          <p>{error}</p>
          <button onClick={retry}>Retry</button>
        </div>
      </div>
    );
  }

  /*
   * Offer the booking; do not make it.
   *
   * This panel used to call attemptAutoBook the moment it mounted, so opening
   * the card to see who was available was itself the act of booking it. Nothing
   * was asked and there was nothing to cancel. Today that only writes a
   * reference against the deal, but it stands in for a real request to a real
   * vendor, and once that is wired an accidental open is somebody driving to
   * an address.
   */
  if (bookingStatus === 'offer') {
    const candidate = vendors[0]
    return (
      <div className="inspection-container">
        <div className="auto-booking-offer">
          <h3>Request an inspection</h3>
          <p className="offer-note">
            An inspector is available for this vehicle. Nothing has been sent
            yet - this records the request against the deal, and once vendor
            booking is connected it asks them to attend.
          </p>
          <button
            type="button"
            onClick={() => {
              if (!candidate) return
              setBookingStatus('auto')
              void attemptAutoBook(candidate)
            }}
            disabled={!candidate}
          >
            Request this inspection
          </button>
          <button type="button" onClick={() => setBookingStatus('manual')}>
            Book by hand instead
          </button>
        </div>
      </div>
    )
  }

  if (bookingStatus === 'success') {
    return (
      <div className="inspection-container">
        <div className="success-state">
          <div className="success-icon">{deliveryWarnings.length ? '!' : '✓'}</div>
          {/* "Inspection Booked" was a promise this screen could not keep — the
              vendor has been asked, not agreed. It now says what actually
              happened. */}
          <h3>{deliveryWarnings.length ? 'Requested, with problems' : 'Inspection requested'}</h3>
          <p>Vendor: <strong>{autoBookResult.vendor}</strong></p>
          <p>Reference: <strong>{autoBookResult.reference}</strong></p>
          <p>Phone: <strong>{autoBookResult.phone}</strong></p>
          {/*
            The auto path does not send anything - it records the request and stops.
            It shared this success screen with the manual path, so it inherited the
            manual path’s sentence and told the user an inspector had been emailed
            when no email existed. deliveryWarnings only ever gets filled in by the
            manual path, so "no warnings" did not mean "everything was sent" - it
            meant "nobody ever checked". Ask the method, not the absence of warnings.
          */}
          {autoBookResult.method === 'auto' ? (
            <p className="manual-note">
              Nothing has been sent yet. The request is recorded here against
              {' '}<strong>{autoBookResult.reference}</strong>, but this vendor has no
              booking link, so call them on the number above to agree a time.
            </p>
          ) : deliveryWarnings.length === 0 ? (
            <p className="manual-note">
              The inspector has been emailed and the seller has been texted. The time is not confirmed
              until the inspector replies.
            </p>
          ) : null}
          {deliveryWarnings.length > 0 && (
            <div className="delivery-warnings">
              <p><strong>The booking is saved, but not everyone was told:</strong></p>
              <ul>
                {deliveryWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (bookingStatus === 'auto' && autoBookResult === null) {
    return (
      <div className="inspection-container">
        <div className="auto-booking-card">
          <h3>Auto-Booking in Progress</h3>
          <p>Contacting inspector...</p>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="inspection-container">
      <div className="manual-booking-card">
        <h2>Book Inspection</h2>

        <div className="booking-info">
          <div className="info-box">
            <h4>Vehicle</h4>
            <p>{vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}</p>
            <p className="vin">VIN: {vehicleInfo.vin}</p>
          </div>

          <div className="info-box">
            <h4>Seller</h4>
            <p>{sellerInfo.name}</p>
            <p className="contact">{sellerInfo.phone}</p>
          </div>

          <div className="info-box">
            <h4>Location</h4>
            <p>{sellerLocation || 'Not provided'}</p>
          </div>
        </div>

        {vendors.length === 0 ? (
          <div className="warning-message">
            ⚠️ No active inspectors. Nothing can be booked or emailed from here, and the
            confirm button stays disabled, until an inspector is added or switched back on.
            If an inspection is arranged another way, record the outcome on the deal so the
            pack is not left looking untouched.
          </div>
        ) : (
          <>
            <div className="vendor-selection">
              <label className="form-label">Select Inspector:</label>
              <div className="vendor-list">
                {vendors.map(vendor => (
                  <label key={vendor.id} className="vendor-option">
                    <input
                      type="radio"
                      name="vendor"
                      value={vendor.id}
                      checked={selectedVendor?.id === vendor.id}
                      onChange={() => setSelectedVendor(vendor)}
                    />
                    <div className="vendor-info">
                      <strong>{vendor.vendor_name}</strong>
                      <span className="state-badge">{vendor.primary_state}</span>
                      <p>{vendor.phone} • {vendor.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="inspection-scheduling">
              <label className="form-label">Inspection Date:</label>
              <input
                type="date"
                value={suggestedDate}
                onChange={(e) => setSuggestedDate(e.target.value)}
                className="date-input"
              />

              <label className="form-label">Preferred Time:</label>
              <input
                type="time"
                value={suggestedTime}
                onChange={(e) => setSuggestedTime(e.target.value)}
                className="time-input"
              />
            </div>

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            <div className="booking-actions">
              <button
                className="btn-book-inspection"
                onClick={handleManualBooking}
                disabled={!selectedVendor || isSubmitting}
              >
                {isSubmitting ? 'Booking...' : 'Send Booking Invitation'}
              </button>
              <p className="action-note">
                We'll send the inspection details to the selected inspector and confirm with the seller.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InspectionBooking;
